import { Button, Typography } from '@cannect/new-components/atoms'
import { useRateConversation } from '@cannect/services/resources/supportChat'
import { useState } from 'react'
import { FiCheckCircle } from 'react-icons/fi'

const SCORES = Array.from({ length: 11 }, (_, index) => index)

/**
 * Cor da nota segundo a régua do NPS.
 *
 * Detratores (0–6), neutros (7–8) e promotores (9–10) já são a leitura padrão
 * do indicador — colorir por outra faixa faria a tela contar uma história
 * diferente do relatório.
 */
const scoreTone = (score: number, isSelected: boolean) => {
  if (!isSelected) return 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
  if (score <= 6) return 'border-red-500 bg-red-500 text-white'
  if (score <= 8) return 'border-amber-500 bg-amber-500 text-white'

  return 'border-primary-700 bg-primary-700 text-white'
}

type SatisfactionSurveyProps = {
  conversationUuid: string
  /** Nota já registrada — some o formulário e vira agradecimento. */
  answeredScore: number | null
}

const SatisfactionSurvey = ({ conversationUuid, answeredScore }: SatisfactionSurveyProps) => {
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')

  const rate = useRateConversation()

  const isAnswered = answeredScore !== null || rate.isSuccess

  if (isAnswered) {
    return (
      <section
        aria-label="Avaliação enviada"
        className="flex flex-col items-center gap-2 rounded-2xl border border-primary-100 bg-primary-50 px-4 py-5 text-center">
        <FiCheckCircle className="text-primary-700" size={22} aria-hidden />
        <Typography.Text type="paragraphTwo" weight="semibold" className="text-primary-900">
          Obrigado pela avaliação!
        </Typography.Text>
        <Typography.Text type="captionOne" className="text-primary-800">
          Sua resposta ajuda a melhorar o atendimento.
        </Typography.Text>
      </section>
    )
  }

  return (
    <section
      aria-label="Pesquisa de satisfação"
      className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-sm">
      <div className="flex flex-col gap-1">
        <Typography.Text type="paragraphTwo" weight="semibold">
          De 0 a 10, o quanto você recomendaria nosso atendimento?
        </Typography.Text>
        <Typography.Text type="captionOne" muted>
          Leva menos de um minuto.
        </Typography.Text>
      </div>

      {/* radiogroup, não botões soltos: leitor de tela anuncia "opção 7 de 11". */}
      <div role="radiogroup" aria-label="Nota de 0 a 10" className="grid grid-cols-6 gap-1.5 sm:grid-cols-11">
        {SCORES.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={score === value}
            aria-label={`Nota ${value}`}
            onClick={() => setScore(value)}
            className={`flex h-9 items-center justify-center rounded-lg border text-xs font-semibold transition ${scoreTone(
              value,
              score === value
            )}`}>
            {value}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between px-0.5">
        <Typography.Text type="captionOne" muted>
          Não recomendaria
        </Typography.Text>
        <Typography.Text type="captionOne" muted>
          Recomendaria muito
        </Typography.Text>
      </div>

      {score !== null && (
        <>
          <label htmlFor="nps-comment" className="flex flex-col gap-1">
            <Typography.Text type="captionOne" muted>
              Quer contar o motivo? (opcional)
            </Typography.Text>
            <textarea
              id="nps-comment"
              rows={2}
              maxLength={500}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="O que poderia ter sido melhor?"
              className="resize-none rounded-xl border border-neutral-300 px-3 py-2 font-sans text-sm outline-none focus:border-primary-700"
            />
          </label>

          <Button
            size="md"
            full
            disabled={rate.isPending}
            onClick={() => rate.mutate({ uuid: conversationUuid, score, comment: comment.trim() || undefined })}>
            {rate.isPending ? 'Enviando…' : 'Enviar avaliação'}
          </Button>
        </>
      )}

      {rate.isError && (
        <Typography.Text type="captionOne" className="text-center text-red-600">
          Não conseguimos registrar sua avaliação. Tente de novo.
        </Typography.Text>
      )}
    </section>
  )
}

export default SatisfactionSurvey
